import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { groupBy } from 'lodash'
import moment from 'moment';
import { Filesystem } from '@capacitor/core';

interface WarehousesInternalListState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    semiLoaded: boolean;
    semiLoading: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
    semiDocuments: any[];
    days: any[];
    schema: any;

    meta: any;
}

const state: WarehousesInternalListState = {
    loading: false,
    loaded: false,
    fatalError: false,

    semiLoading: false,
    semiLoaded: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
    semiDocuments: [],
    days: [],
    schema: null,

    meta: {},
};

export const ITEMS_LIST = 'GET_WAREHOUSE_INTERNAL_LIST';
export const SEMI_ITEMS_LIST = 'GET_WAREHOUSE_SEMI_LIST';

export const actions: ActionTree<WarehousesInternalListState, RootState> = {
    loadData({ commit, getters, dispatch }, query) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('CLEAR_DATA');
            commit('START_LOADING');

            const columns: any = {
                'base_id': 'vwibc.base_id',
                'base_name': 'vwibc.base_name',
                'weight': 'vwibc.weight',
                'data': 'vwibc.data',
            };

            const filter: any[] = [];

            vm.$db([
                vm.$q(ITEMS_LIST)
                    .raw('documents.list')
                    .action({
                        "columns": columns,
                        "from": "s:view_warehouse_internal_volumes_filling",
                        "from_as": "vwibc",
                        "joins": [
                        ],
                        ...(
                            filter.length > 1 ?
                            {
                                "where": {
                                    "$and": filter
                                }
                            } : {
                                "where": filter[0]
                            }
                        )
                    }),
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('SET_WAREHOUSE_DATA', result.getOperation(ITEMS_LIST).getContent());
                commit('LOADED_SUCCESS');

                resolve();
            }));
        })
    },

    loadSemiData({ commit, state }) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_SEMI_LOADING');
            console.debug('[WarehousesInternal] load semi products');

            vm.$db([
                vm.$q(SEMI_ITEMS_LIST)
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'scf.id',
                            number: 'scp.number',
                            product_id: 'scp.product_id',
                            product_name: 'p.name',
                            container_name: 'pc.name',
                            quantity: 'scf.quantity',
                            shift_id: 'scp.shift_id',
                            shift_date: 's.shift_date',
                        },
                        from: 's:shift_custom_plan',
                        from_as: 'scp',
                        joins: [
                            {
                                join: 's:shifts',
                                type: 'left',
                                as: 's',
                                on: {
                                    $eq: ['s.id', 'scp.shift_id']
                                }
                            },
                            {
                                join: 's:shift_custom_fact',
                                as: 'scf',
                                on: {
                                    $eq: ['scf.plan_id', 'scp.id']
                                }
                            },
                            {
                                join: 's:products',
                                type: 'left',
                                as: 'p',
                                on: {
                                    $eq: ['scp.product_id', 'p.id']
                                }
                            },
                            {
                                join: 's:product_containers',
                                type: 'left',
                                as: 'pc',
                                on: {
                                    $eq: ['scf.product_container_id', 'pc.id']
                                }
                            },
                        ],
                        order: [
                            {$asc: ['scp.number']}
                        ],
                        page_size: 1000,
                        where: {
                            $and: [
                                {$eq: ['scp.kind', {$: 'semi'}]},
                                {$eq: ['scf.is_done', {$: true}]},
                            ]
                        },
                    }),
            ]).then(((result: ServerResult) => {
                const opResult = result.getOperation(SEMI_ITEMS_LIST);
                commit('SET_SEMI_DATA', opResult.getContent());
                commit('SEMI_LOADED_SUCCESS');
                console.debug('[WarehousesInternal] loaded semi products', opResult.getContent().documents.length);

                resolve();
            }));
        })
    },

};

export const mutations: MutationTree<WarehousesInternalListState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => { 
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },
    CLEAR_DATA(state, data) {
        state.days = []
        state.documents =[]
        state.semiDocuments = []
        state.semiLoaded = false
    },

    START_SEMI_LOADING: (state) => {
        state.semiLoaded = false;
        state.semiLoading = true;
    },
    SEMI_LOADED_SUCCESS: (state) => {
        state.semiLoaded = true;
        state.semiLoading = false;
    },

    SET_SEMI_DATA(state, data) {
        const groups: { [key: string]: any } = {};
        data.documents.forEach((item: any) => {
            const key = item.product_id != null ? item.product_id : item.product_name;
            if (!groups[key]) {
                groups[key] = {
                    product_id: item.product_id,
                    product_name: item.product_name,
                    weight: 0,
                    data: [],
                };
            }
            groups[key].weight += parseFloat(item.quantity) || 0;
            groups[key].data.push(item);
        });
        state.semiDocuments = Object.values(groups)
            .sort((a: any, b: any) => a.product_name.localeCompare(b.product_name));
    },

    SET_WAREHOUSE_DATA(state, data) {
        data.documents = data.documents.reduce((acc: any[], item: any) => {
            const _data: any[] = [];
            Object.keys(item.data).map((key: any) => {
                const row = item.data[key];
                Object.values(row.filled_rows).map((fill: any, idx: number) => {
                    const _n = {...row}
                    _n.filled_rows = [fill]
                    _n.filled_rows_count = row.filled_rows.length
                    _n.filled_rows_idx = idx
                    _data.push(_n)
                    
                })
            })
            item.data = _data
            acc.push(item)
            return acc;
        }, [])
        state.documents = data.documents

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    },
};

const getters = {
    total(state: WarehousesInternalListState) {
        return {
            quantity: state.documents.reduce((prev: any, field: any) => {
                return prev + parseInt(field.quantity)
            }, 0),
            total_quantity: state.documents.reduce((prev: any, field: any) => {
                return prev + (parseInt(field.quantity) * parseFloat(field.product_container_weight))
            }, 0),
            columns:  state.documents.reduce((prev: any, field: any) => {
                field.columns.map((item: any, idx: number) => {
                    console.log('=>', item, idx)
                    if (!prev[idx]) {
                        prev[idx] = {
                            'expected': 0,
                            'remains': 0,
                            'availability': 0,
                            'remains_total': 0,
                        }
                    }
                    prev[idx].expected += parseFloat(item.expected)
                    prev[idx].remains += parseFloat(item.remains)
                    prev[idx].availability += parseFloat(item.availability)
                    prev[idx].remains_total += parseFloat(item.remains_total)
                })
                return prev
            }, [])
        }
    },
    exportData(state: WarehousesInternalListState) {
        const csvContent: any[] = [];
        const header = ['База', 'Тара', 'Кол-во, кг', 'Кол-во ведер', ]

        state.documents.map((item: any) => {
            const row: any[] = [];
            row.push(item.base_name)
            row.push(item.container_name)
            row.push(item.weight)
            row.push(item.quantity)
            csvContent.push(row)
        })
        return [
            header,
            ...csvContent
        ]
      },
    exportSemiData(state: WarehousesInternalListState) {
        const csvContent: any[] = [];
        const header = ['№ партии', 'Дата смены', 'Наименование', 'Тара', 'Количество', ]

        state.semiDocuments.map((item: any) => {
            item.data.map((fact: any) => {
                const row: any[] = [];
                row.push(fact.number)
                row.push(fact.shift_date ? moment(fact.shift_date).format('DD.MM.YYYY') : '')
                row.push(fact.product_name)
                row.push(fact.container_name)
                row.push(fact.quantity)
                csvContent.push(row)
            })
        })
        return [
            header,
            ...csvContent
        ]
      }
};

export const STORE_KEY = '$_warehouses_internal_list';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
